import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import type { IconName, IconSize } from '@lqa-frontend/ui-system';
import { buttonVariants } from '../buttons/Button.tsx';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';

type AlertDialogVariant = 'default' | 'danger' | 'warning' | 'success';

interface AlertDialogContextValue {
	variant: AlertDialogVariant;
	shouldShowIcon: boolean;
}

type AlertDialogProps = React.ComponentProps<
	typeof AlertDialogPrimitive.Root
> & {
	variant?: AlertDialogVariant;
	shouldShowIcon?: boolean;
};

const AlertDialogContext = React.createContext<AlertDialogContextValue>({
	variant: 'default',
	shouldShowIcon: false,
});

function AlertDialog({
	variant = 'default',
	shouldShowIcon = false,
	...props
}: Readonly<AlertDialogProps>) {
	return (
		<AlertDialogContext.Provider value={{ variant, shouldShowIcon }}>
			<AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
		</AlertDialogContext.Provider>
	);
}

function AlertDialogTrigger({
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
	return (
		<AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
	);
}

function AlertDialogPortal({
	container,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
	const resolvedContainer = useResolvedPortalContainer(container);
	return (
		<AlertDialogPrimitive.Portal
			data-slot="alert-dialog-portal"
			{...props}
			container={resolvedContainer}
		/>
	);
}

function AlertDialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
	return (
		<AlertDialogPrimitive.Overlay
			data-slot="alert-dialog-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[80] bg-black/50',
				className
			)}
			{...props}
		/>
	);
}

interface IAlertDialogIconConfig {
	iconName: IconName;
	containerClassName: string;
	iconClassName: string;
	containerSize: string;
	iconSize: IconSize;
}

interface IAlertDialogVariantIconConfig {
	default: IAlertDialogIconConfig;
	danger: IAlertDialogIconConfig;
	warning: IAlertDialogIconConfig;
	success: IAlertDialogIconConfig;
}

const variantIconConfig: IAlertDialogVariantIconConfig = {
	default: {
		iconName: 'infoLg',
		containerClassName: 'bg-interactive-light',
		iconClassName: 'text-interactive',
		containerSize: 'size-30',
		iconSize: 's',
	},
	danger: {
		iconName: 'exclamationTriangle',
		containerClassName: 'bg-danger-light',
		iconClassName: 'text-text-danger',
		containerSize: 'size-30',
		iconSize: 's',
	},
	warning: {
		iconName: 'infoCircle',
		containerClassName: 'bg-warning-light',
		iconClassName: 'text-warning',
		containerSize: 'size-30',
		iconSize: 's',
	},
	success: {
		iconName: 'checkLg',
		containerClassName: 'bg-success-light',
		iconClassName: 'text-text-success',
		containerSize: 'size-30',
		iconSize: 's',
	},
};

function AlertDialogContent({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
	const { variant, shouldShowIcon } = React.useContext(AlertDialogContext);
	const iconConfig = variantIconConfig[variant];

	return (
		<AlertDialogPortal>
			<AlertDialogOverlay />
			<AlertDialogPrimitive.Content
				data-slot="alert-dialog-content"
				className={cn(
					'bg-base-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-[90] md:w-512 translate-x-[-50%] translate-y-[-50%] rounded-m border border-outline-neutral p-24 shadow-lg duration-200 ',
					shouldShowIcon ? 'flex gap-16' : 'grid gap-16',
					className
				)}
				{...props}
			>
				{shouldShowIcon && (
					<div
						className={cn(
							'rounded-m flex items-center justify-center shrink-0',
							iconConfig.containerSize,
							iconConfig.containerClassName
						)}
					>
						<Icon
							name={iconConfig.iconName}
							size={iconConfig.iconSize}
							className={iconConfig.iconClassName}
						/>
					</div>
				)}
				<div
					className={cn(
						shouldShowIcon ? 'flex flex-col gap-16 flex-1' : 'contents'
					)}
				>
					{props.children}
				</div>
				<AlertDialogClose />
			</AlertDialogPrimitive.Content>
		</AlertDialogPortal>
	);
}

function AlertDialogHeader({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-dialog-header"
			className={cn('flex flex-col gap-8 text-center sm:text-left', className)}
			{...props}
		/>
	);
}

function AlertDialogFooter({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-dialog-footer"
			className={cn('flex gap-12 flex-row justify-end', className)}
			{...props}
		/>
	);
}

function AlertDialogTitle({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
	return (
		<AlertDialogPrimitive.Title
			data-slot="alert-dialog-title"
			className={cn('text-20 font-semibold', className)}
			{...props}
		/>
	);
}

function AlertDialogDescription({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
	return (
		<AlertDialogPrimitive.Description
			data-slot="alert-dialog-description"
			className={cn('text-neutral text-14', className)}
			{...props}
		/>
	);
}

const variantButtonVariantMap: Record<
	AlertDialogVariant,
	'default' | 'danger' | 'warning' | 'success'
> = {
	default: 'default',
	danger: 'danger',
	warning: 'warning',
	success: 'success',
};

type AlertDialogActionProps = React.ComponentProps<
	typeof AlertDialogPrimitive.Action
> & {
	state?: 'idle' | 'loading' | 'success' | 'error' | 'pending';
};

function AlertDialogAction({
	className,
	...props
}: Readonly<AlertDialogActionProps>) {
	const { variant } = React.useContext(AlertDialogContext);
	const buttonVariant = variantButtonVariantMap[variant];

	return (
		<AlertDialogPrimitive.Action
			className={
				!props.asChild
					? cn(buttonVariants({ variant: buttonVariant, size: 'm' }), className)
					: undefined
			}
			{...props}
		/>
	);
}

function AlertDialogCancel({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
	return (
		<AlertDialogPrimitive.Cancel
			className={cn(
				buttonVariants({ variant: 'outline', size: 'm' }),
				className
			)}
			{...props}
		/>
	);
}

function AlertDialogLink({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
	return (
		<AlertDialogPrimitive.Action
			className={
				!props.asChild
					? cn(buttonVariants({ variant: 'link', size: 'm' }), className)
					: undefined
			}
			{...props}
		/>
	);
}

function AlertDialogClose({
	className,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
	return (
		<AlertDialogPrimitive.Cancel
			className={cn(
				'absolute top-10 cursor-pointer right-10 flex items-center justify-center rounded-s p-6 hover:bg-base-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-outline-interactive',
				className
			)}
			{...props}
		>
			<Icon name="xLg" className="text-text-secondary" />
			<span className="sr-only">Close</span>
		</AlertDialogPrimitive.Cancel>
	);
}

export {
	AlertDialog,
	AlertDialogPortal,
	AlertDialogOverlay,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogLink,
	AlertDialogClose,
};
